import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import {
  Mail,
  User,
  Phone,
  ArrowLeft,
  Loader2,
  MapPin,
  GraduationCap,
} from 'lucide-react';
import AuthLayout from './AuthLayout';
import AuthFormField, { authInputWithIconClass, authInputPlainClass } from './AuthFormField';
import AuthPasswordField from './AuthPasswordField';
import { cardTheme } from '../../styles/cardTheme';

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  active: boolean;
}

interface GradesResponse {
  success: boolean;
  message: string;
  data: Grade[];
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-green-800">
    <span className="h-4 w-1 rounded-full bg-gold-500" />
    {children}
  </h2>
);

const Register: React.FC = () => {
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [governorate, setGovernorate] = useState('');
  const [center, setCenter] = useState('');
  const [error, setError] = useState('');
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGrades = async () => {
      setLoadingGrades(true);
      try {
        const response = await axios.get<GradesResponse>(`${BASE_URL}api/Grade`, {
          headers: { ...getTenantHeaders() },
        });
        if (response.data.success && response.data.data) {
          setGrades(response.data.data.filter((grade) => grade.active));
        } else {
          setGrades([]);
        }
      } catch {
        setGrades([]);
      } finally {
        setLoadingGrades(false);
      }
    };
    fetchGrades();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }
    if (!academicYear) {
      setError('الرجاء اختيار الصف الدراسي');
      return;
    }
    if (!phoneNumber) {
      setError('رقم الهاتف مطلوب');
      return;
    }
    if (!parentPhone) {
      setError('رقم هاتف ولي الأمر مطلوب');
      return;
    }
    if (!userName) {
      setError('اسم المستخدم مطلوب');
      return;
    }

    const phoneRegex = /^01[0-9]{9}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setError('يرجى إدخال رقم هاتف مصري صحيح');
      return;
    }
    if (!phoneRegex.test(parentPhone)) {
      setError('يرجى إدخال رقم هاتف ولي الأمر صحيح');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('UserName', userName);
      formData.append('Email', email);
      formData.append('Password', password);
      formData.append('ConfirmPassword', confirmPassword);
      formData.append('PhoneNumber', phoneNumber);
      formData.append('ParentPhone', parentPhone);
      formData.append('GradeId', academicYear);
      formData.append('Governorate', governorate);
      formData.append('Center', center);

      const result = await register(formData);

      if (result.success) {
        navigate('/login');
      } else {
        setError(result.error || 'حدث خطأ في إنشاء الحساب');
      }
    } catch {
      setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
    }
  };

  return (
    <AuthLayout
      wide
      title="انضم لمنصتنا"
      subtitle="أنشئ حسابك وابدأ رحلتك التعليمية"
      alternateLink={{
        prompt: 'لديك حساب بالفعل؟',
        label: 'تسجيل الدخول',
        to: '/login',
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <section>
          <SectionHeading>البيانات الشخصية</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <AuthFormField label="اسم المستخدم" htmlFor="userName" icon={User}>
              <input
                id="userName"
                name="userName"
                type="text"
                required
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className={authInputWithIconClass}
                placeholder="الاسم الكامل"
              />
            </AuthFormField>

            <AuthFormField label="البريد الإلكتروني" htmlFor="email" icon={Mail}>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInputWithIconClass}
                placeholder="example@email.com"
              />
            </AuthFormField>

            <AuthFormField label="رقم الهاتف" htmlFor="phoneNumber" icon={Phone}>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                required
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className={authInputWithIconClass}
                placeholder="01xxxxxxxxx"
              />
            </AuthFormField>

            <AuthFormField label="رقم هاتف ولي الأمر" htmlFor="parentPhone" icon={Phone}>
              <input
                id="parentPhone"
                name="parentPhone"
                type="tel"
                required
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                className={authInputWithIconClass}
                placeholder="01xxxxxxxxx"
              />
            </AuthFormField>
          </div>
        </section>

        <section>
          <SectionHeading>الموقع والصف الدراسي</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <AuthFormField label="المحافظة" htmlFor="governorate" icon={MapPin}>
              <select
                id="governorate"
                required
                value={governorate}
                onChange={(e) => setGovernorate(e.target.value)}
                className={authInputWithIconClass}
              >
                <option value="">اختر المحافظة</option>
                <option value="القاهرة">القاهرة</option>
                <option value="الجيزة">الجيزة</option>
                <option value="الإسكندرية">الإسكندرية</option>
                <option value="الدقهلية">الدقهلية</option>
                <option value="البحيرة">البحيرة</option>
                <option value="المنيا">المنيا</option>
                <option value="أسيوط">أسيوط</option>
                <option value="سوهاج">سوهاج</option>
                <option value="قنا">قنا</option>
                <option value="الأقصر">الأقصر</option>
                <option value="أسوان">أسوان</option>
                <option value="الشرقية">الشرقية</option>
                <option value="الغربية">الغربية</option>
                <option value="كفر الشيخ">كفر الشيخ</option>
                <option value="المنوفية">المنوفية</option>
                <option value="الفيوم">الفيوم</option>
                <option value="بني سويف">بني سويف</option>
                <option value="الإسماعيلية">الإسماعيلية</option>
                <option value="بورسعيد">بورسعيد</option>
                <option value="السويس">السويس</option>
                <option value="شمال سيناء">شمال سيناء</option>
                <option value="جنوب سيناء">جنوب سيناء</option>
                <option value="البحر الأحمر">البحر الأحمر</option>
                <option value="الوادي الجديد">الوادي الجديد</option>
                <option value="مطروح">مطروح</option>
                <option value="دمياط">دمياط</option>
                <option value="القليوبية">القليوبية</option>
              </select>
            </AuthFormField>

            <AuthFormField label="المركز" htmlFor="center">
              <input
                id="center"
                name="center"
                type="text"
                required
                value={center}
                onChange={(e) => setCenter(e.target.value)}
                className={authInputPlainClass}
                placeholder="أدخل المركز"
              />
            </AuthFormField>

            <AuthFormField label="الصف الدراسي" htmlFor="academicYear" icon={GraduationCap} className="sm:col-span-2">
              <select
                id="academicYear"
                name="academicYear"
                required
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                disabled={loadingGrades}
                className={authInputWithIconClass}
              >
                <option value="">
                  {loadingGrades ? 'جاري تحميل الصفوف...' : 'اختر الصف الدراسي'}
                </option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </AuthFormField>
          </div>
        </section>

        <section>
          <SectionHeading>بيانات الحساب</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <AuthPasswordField
              id="password"
              label="كلمة المرور"
              value={password}
              onChange={setPassword}
              placeholder="أدخل كلمة المرور"
              autoComplete="new-password"
              required
            />
            <AuthPasswordField
              id="confirmPassword"
              label="تأكيد كلمة المرور"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="أعد إدخال كلمة المرور"
              autoComplete="new-password"
              required
            />
          </div>
        </section>

        <button
          type="submit"
          disabled={isLoading}
          className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-xl py-3 text-base font-semibold shadow-md shadow-green-900/10 transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70 ${cardTheme.btnPrimary}`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              جاري إنشاء الحساب...
            </>
          ) : (
            <>
              إنشاء الحساب
              <ArrowLeft className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Register;
